import { MouseEvent, useState } from 'react'
import { Position } from 'css-box-model'

import { ActionData, HandlerData, MetaData, NestedMenuProps } from '@components/NestedMenu'
import { isBottomPinnedTotalRow, isGroupTitleRow } from '@components/TimeLineGrid/helpers'

import { CONTEXT_MENU_APPLICABLE_TO, MENU_POINT_TYPES } from '@constants'
import {
  HandleTimelineCellContextMenu,
  MenuPointOptionType,
  ObjectDataRecord,
  SelectedCell,
} from '@types'

type UseContextMenuHandlersType = {
  onOpenContextMenu: (event: MouseEvent<HTMLDivElement>) => void
  onClickLink: (item: MenuPointOptionType, position?: Position) => void
  onExecuteCommand: (data: any) => void
  onExecuteAction: (data: MetaData & HandlerData) => void
  onClearSelectedCells: () => void
}

export const useContextMenuHandlers = (props: UseContextMenuHandlersType) => {
  const {
    onExecuteAction,
    onOpenContextMenu,
    onClickLink,
    onExecuteCommand,
    onClearSelectedCells,
  } = props

  const [contextMenuData, setContextMenuData] = useState<Omit<NestedMenuProps, 'onClose'>>({
    items: [],
    dataTransfer: {
      changes: {},
      config: {},
      data: {},
      metaData: {},
    },
    onItemClick: undefined,
  })

  const handleContextMenu: HandleTimelineCellContextMenu = data => {
    const { action, event, params, cellItem, cellContextRef, isOpenedFromSelectedCell } = data

    const columnId = params.field
    const rowId = params.id

    if (!isGroupTitleRow(rowId as string) && !isBottomPinnedTotalRow(rowId as string) && columnId) {
      event.preventDefault()

      const dates = columnId.slice(1, -1).split(';')

      onOpenContextMenu(event)
      setContextMenuData({
        items: action.contextMenu?.menuPoints || [],
        dataTransfer: {
          changes: {},
          config: action.contextMenu,
          data: {},
          metaData: {
            contextMenuConfig: action.contextMenu,
            srcObj: {
              rowData: params.row,
              columnId,
              rowId,
              cellValue: params.value,
              startDate: dates[0],
              endDate: dates[1],
              variables: action?.contextMenu?.staticVariables,
              cellItem,
              selectedCells: cellContextRef.current.selectedCells,
              isOpenedFromSelectedCell,
            },
          },
        },
        onItemClick: handleItemClick,
      })
    }
  }

  const handleItemClick = (data: ActionData) => {
    switch (data.metaData.menuItem.menuPointType) {
      case MENU_POINT_TYPES.LINK:
        onClickLink(data.metaData.menuItem)

        return
      case MENU_POINT_TYPES.COMMAND:
        if (
          data.metaData.contextMenuConfig?.applicableTo === CONTEXT_MENU_APPLICABLE_TO.TIME_TABLE &&
          data.metaData.srcObj.isOpenedFromSelectedCell
        ) {
          const events: ObjectDataRecord[] = []

          onClearSelectedCells()

          Object.values(data.metaData.srcObj.selectedCells as SelectedCell[]).forEach(cell => {
            if (data.metaData.menuItem.useCommandOnEvent) {
              cell.events.forEach((event: ObjectDataRecord) => {
                events.push({
                  cellItem: event,
                  ...cell,
                })
              })

              return
            }

            events.push(cell)
          })

          events.forEach(event => {
            onExecuteCommand({ ...data.metaData, srcObj: event })
          })

          return
        }

        onExecuteCommand(data.metaData)

        return
      case MENU_POINT_TYPES.ACTION:
        onExecuteAction(data.metaData)

        return
    }
  }

  return {
    handleContextMenu,
    contextMenuData,
  }
}
